import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Paperclip, Save, Lock, FileText as FileIcon, Image as ImageIcon, Edit2 } from 'lucide-react';
import { API } from '../App';

const URL_OR_PHONE_REGEX = /(https?:\/\/[^\s]+|\+[\d\s()-]{6,17}\d|\b\d{11}\b)/g;
const TRAILING_PUNCT_REGEX = /[.,;:!?)\]}»"']+$/;

function linkify(text) {
  return text.split(URL_OR_PHONE_REGEX).map((part, i) => {
    if (i % 2 === 0) return part;
    if (part.startsWith('+')) {
      const digits = part.replace(/\D/g, '');
      return (
        <a key={i} href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer noopener" className="text-green-700 hover:text-green-800 underline">
          {part}
        </a>
      );
    }
    if (/^\d{11}$/.test(part)) {
      if (part[2] === '9') {
        return (
          <a key={i} href={`https://wa.me/55${part}`} target="_blank" rel="noreferrer noopener" className="text-green-700 hover:text-green-800 underline">
            {part}
          </a>
        );
      }
      return part;
    }
    const trailMatch = part.match(TRAILING_PUNCT_REGEX);
    const trail = trailMatch ? trailMatch[0] : '';
    const url = trail ? part.slice(0, -trail.length) : part;
    return (
      <React.Fragment key={i}>
        <a href={url} target="_blank" rel="noreferrer noopener" className="underline break-all" style={{ color: '#1E3A8A' }}>{url}</a>
        {trail}
      </React.Fragment>
    );
  });
}

const WEEKDAYS = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateLabel(dataRef) {
  const [y, m, d] = dataRef.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y} — ${WEEKDAYS[dt.getDay()]}`;
}

function statusStyle(dataRef, conteudo) {
  const today = todayStr();
  const preenchido = !!(conteudo && conteudo.trim());
  if (dataRef > today) return { border: '#DBEAFE', bg: '#EEF2FF', dot: '#93c5fd', label: 'Futuro — bloqueado', opacity: true };
  if (dataRef === today) return preenchido
    ? { border: '#86efac', bg: '#ffffff', dot: '#22c55e', label: 'Preenchido hoje', opacity: false }
    : { border: '#fdba74', bg: '#ffffff', dot: '#f97316', label: 'Pendente hoje — urgente', opacity: false };
  return preenchido
    ? { border: '#DBEAFE', bg: '#ffffff', dot: '#93c5fd', label: 'Preenchido', opacity: false }
    : { border: '#fca5a5', bg: '#ffffff', dot: '#ef4444', label: 'Atrasado — vazio', opacity: false };
}

const AgendaDiaryCard = React.forwardRef(function AgendaDiaryCard({ entry, editable, onSave, onUpload, onDirtyChange }, ref) {
  const [conteudo, setConteudo] = useState(entry.conteudo || '');
  const [isEditing, setIsEditing] = useState(!entry.conteudo);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const style = statusStyle(entry.data_referencia, entry.conteudo);
  const isFuture = entry.data_referencia > todayStr();

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const handleSaveClick = async () => {
    setSaving(true);
    try {
      await onSave(entry.data_referencia, conteudo);
      setDirty(false);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save: handleSaveClick }));

  const startEditing = () => {
    setConteudo(entry.conteudo || '');
    setIsEditing(true);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      await onUpload(entry.data_referencia, files);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="rounded-xl p-4 space-y-3 border"
      style={{ backgroundColor: style.bg, borderColor: style.border, opacity: style.opacity ? 0.6 : 1 }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: style.dot }} />
          <span className="text-sm font-medium text-black">{formatDateLabel(entry.data_referencia)}</span>
        </div>
        <span className="text-xs" style={{ color: '#1E3A8A', opacity: 0.6 }}>{style.label}</span>
      </div>

      {isFuture ? (
        <p className="text-xs flex items-center gap-2" style={{ color: '#1E3A8A', opacity: 0.5 }}>
          <Lock size={12} /> Disponível após 00:00 do dia
        </p>
      ) : editable && isEditing ? (
        <>
          <textarea
            className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none min-h-[80px] resize-y"
            style={{ backgroundColor: '#EEF2FF', border: '1px solid #DBEAFE', color: '#000' }}
            value={conteudo}
            maxLength={5000}
            onChange={e => { setConteudo(e.target.value); setDirty(true); }}
            placeholder="Descreva o andamento do dia..."
          />
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: '#1E3A8A' }}>
              <Paperclip size={13} />
              {uploading ? 'Enviando...' : 'Anexar arquivo'}
              <input
                type="file" accept="image/jpeg,image/png,application/pdf" multiple
                className="hidden" onChange={handleFiles} disabled={uploading}
              />
            </label>
            <button
              onClick={handleSaveClick}
              disabled={!dirty || saving}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 text-white"
              style={{ backgroundColor: '#1E3A8A' }}
            >
              <Save size={13} /> {saving ? 'Salvando...' : 'Salvar registro'}
            </button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-black whitespace-pre-wrap">
            {entry.conteudo ? linkify(entry.conteudo) : <span className="italic" style={{ color: '#1E3A8A', opacity: 0.35 }}>Sem registro</span>}
          </p>
          {editable && (
            <button
              onClick={startEditing}
              className="flex items-center gap-1.5 text-xs transition-colors"
              style={{ color: '#1E3A8A', opacity: 0.6 }}
            >
              <Edit2 size={12} /> Editar
            </button>
          )}
        </div>
      )}

      {entry.anexos?.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {entry.anexos.map((a, i) => (
            <a
              key={i} href={`${API}${a.path}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs"
              style={{ backgroundColor: '#EEF2FF', border: '1px solid #DBEAFE', color: '#1E3A8A' }}
            >
              {a.tipo_mime === 'application/pdf' ? <FileIcon size={12} /> : <ImageIcon size={12} />}
              <span className="truncate max-w-[120px]">{a.nome_original}</span>
            </a>
          ))}
        </div>
      )}

      {entry.preenchido_por_nome && (
        <p className="text-[11px]" style={{ color: '#1E3A8A', opacity: 0.5 }}>Preenchido por {entry.preenchido_por_nome}</p>
      )}
    </div>
  );
});

export default AgendaDiaryCard;
